import React, { useEffect, useState, useContext, useCallback } from 'react'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { useSwitchChain } from 'wagmi'
import { useTranslation } from 'react-i18next'
import { Button } from './Button'
import {
  getChainId,
  getNetworkName,
  isNetworkSupported,
  truncateAddress,
} from '@/utils/currencies'
import { useTransaction } from '@/hooks/useTransactionListener'
import {
  useWallets,
  useConnect,
  useDisconnect as useSolanaDisconnect,
  type UiWallet,
  type UiWalletAccount,
} from '@wallet-standard/react'
import { SelectedWalletAccountContext } from '@/contexts/WalletAccountProvider'
import * as Toast from '@radix-ui/react-toast'
import * as Icons from '@radix-ui/react-icons'
import * as Dialog from '@radix-ui/react-dialog'
import * as Sentry from '@sentry/react'
import { ConnectedSolanaWalletButton } from './ConnectedSolanaWalletButton'
import { isMobileBrowser, isPhantomBrowser } from '@/utils/phantomMobile'
import { PhantomSolanaMobileContext } from '@/contexts/PhantomSolanaMobileContext'
import { PhantomSolanaMobileConnectButton } from './PhantomSolanaMobileConnectButton'
import { ConnectedPhantomSolanaWalletButton } from './ConnectedPhantomSolanaWalletButton'

const ErrorMessage = ({ message }: { message: string }) => (
  <div
    className="absolute left-0 right-0 mt-1 flex items-center justify-center gap-2 text-red-500 text-xs"
    data-testid="btn-hybridwalletconnect-submit"
  >
    <Icons.ExclamationTriangleIcon className="h-3 w-3" />
    <span>{message}</span>
  </div>
)

interface UnifiedWalletButtonProps {
  amount: number
  recipientAddress: string
  tokenSymbol: string
  senderAddress: string
  orderId: string
  onTransferComplete?: (txHash: string) => void
  onError?: (error: Error) => void
}

export const HybridWalletConnectButton: React.FC<UnifiedWalletButtonProps> = (
  props
) => {
  const { t } = useTranslation()
  const { switchChain } = useSwitchChain()
  const { onError } = props
  const [, network] = props.tokenSymbol.split('-')
  const isSupported = isNetworkSupported(network)
  const [walletDialogOpen, setWalletDialogOpen] = useState(false)

  // EVM transaction handling hook
  const {
    transactionState,
    handleEvmTransfer,
    isEVM,
    isPending,
    isConfirming,
    chainId,
  } = useTransaction(props)

  // New Solana wallet hooks
  const wallets = useWallets()

  // Filter for Solana wallets
  const availableSolanaWallets = wallets.filter((wallet) =>
    wallet.chains.includes('solana:mainnet')
  )

  // Get the selected wallet account from context
  const [selectedWalletAccount, setSelectedWalletAccount] = useContext(
    SelectedWalletAccountContext
  )
  const {
    callbackError: phantomCallbackError,
    disconnect: disconnectPhantom,
    session: phantomSession,
  } = useContext(PhantomSolanaMobileContext)

  // Find the wallet that owns the current account
  const currentWallet = selectedWalletAccount
    ? wallets.find((wallet) =>
        wallet.accounts.some(
          (account) => account.address === selectedWalletAccount.address
        )
      )
    : undefined

  // Create a map of disconnect functions for each wallet
  // We'll use these to disconnect specific wallets
  const walletDisconnectFns = new Map()

  // Setup disconnect functions for each wallet
  wallets.forEach((wallet) => {
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [, disconnectFn] = useSolanaDisconnect(wallet)
      walletDisconnectFns.set(wallet.name, disconnectFn)
    } catch {
      walletDisconnectFns.set(wallet.name, () => {})
    }
  })

  // Transaction processing state for Solana
  const [isSolanaProcessing, setIsSolanaProcessing] = useState(false)

  // Toast state
  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState({
    title: '',
    description: '',
    type: 'info' as 'info' | 'warning' | 'success',
  })

  // Function to show toast notifications
  const showToast = useCallback(
    (
      title: string,
      description: string,
      type: 'info' | 'warning' | 'success'
    ) => {
      setToastMessage({
        title,
        description,
        type,
      })
      setToastOpen(true)
    },
    []
  )

  // Use the ConnectButton component for each wallet
  const WalletConnectButton = ({
    wallet,
    onConnect,
  }: {
    wallet: UiWallet
    onConnect: (accounts: UiWalletAccount) => void
  }) => {
    const [isConnecting, connect] = useConnect(wallet)
    const walletId = wallet.name.toLowerCase().replace(/\s+/g, '-')
    const handleConnectClick = useCallback(async () => {
      try {
        const nextAccounts = await connect()
        // Handle account selection if needed
        if (nextAccounts.length > 0) {
          // Check if the required account is among the connected accounts
          const requiredAccount = nextAccounts.find(
            (account) => account.address === props.senderAddress
          )
          if (requiredAccount) {
            // If we found the exact account needed, use it directly
            onConnect(requiredAccount)
          } else if (nextAccounts.length === 1) {
            // If there's only one account, use it (with a warning if it's not the right one)
            onConnect(nextAccounts[0])
          } else {
            // If there are multiple accounts, show the account selection UI
            console.log('Multiple accounts, showing selection UI')
          }
        }
      } catch (error: any) {
        console.error('Wallet connection error:', error)
        showToast(
          'Connection Failed',
          error.message || 'Failed to connect to wallet',
          'warning'
        )
        Sentry.captureException(error, {
          tags: {
            action: 'wallet_connect',
            wallet_name: wallet.name,
          },
        })
      }
    }, [connect, wallet.accounts, onConnect])
    return (
      <button
        onClick={handleConnectClick}
        disabled={isConnecting}
        className="w-full justify-start p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
        data-testid={`button-wallet-connect-${walletId}`}
      >
        <div
          className="flex items-center gap-2"
          data-testid={`container-wallet-option-${walletId}`}
        >
          {wallet.icon && (
            <img
              src={wallet.icon}
              alt={`${wallet.name} icon`}
              className="w-6 h-6"
              data-testid={`image-wallet-icon-${walletId}`}
            />
          )}
          <span
            className="font-medium"
            data-testid={`text-wallet-name-${walletId}`}
          >
            {wallet.name}
          </span>
          {isConnecting && (
            <span
              className="ml-auto"
              data-testid={`text-wallet-connecting-${walletId}`}
            >
              Connecting...
            </span>
          )}
        </div>
      </button>
    )
  }

  // Handle accounts after successful connection
  const handleAccountsReceived = useCallback(
    (account: UiWalletAccount) => {
      if (account) {
        disconnectPhantom()
        // Set in the context (this will also save to localStorage)
        setSelectedWalletAccount(account)
        showToast('Wallet Connected', `Connected successfully`, 'success')
      } else {
        showToast(
          'Connection Failed',
          'No accounts were returned from the wallet',
          'warning'
        )
      }
      setWalletDialogOpen(false)
    },
    [disconnectPhantom, setSelectedWalletAccount, showToast]
  )

  // Solana wallet selection dialog
  const showPhantomMobile = isMobileBrowser() && !isPhantomBrowser()
  const WalletSelectDialog = () => (
    <Dialog.Root open={walletDialogOpen} onOpenChange={setWalletDialogOpen}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/40 backdrop-blur-sm"
          data-testid="btn-hybridwalletconnect-submit"
        />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 p-6 rounded-lg shadow-xl w-[90vw] max-w-md"
          data-testid="btn-hybridwalletconnect-submit"
        >
          <Dialog.Title
            className="text-xl font-semibold mb-4"
            data-testid="btn-hybridwalletconnect-submit"
          >
            {selectedWalletAccount || phantomSession
              ? 'Switch Wallet'
              : 'Select a Wallet'}
          </Dialog.Title>
          {(selectedWalletAccount || phantomSession) && (
            <div
              className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded border border-yellow-200 dark:border-yellow-800"
              data-testid="btn-hybridwalletconnect-submit"
            >
              <p
                className="text-sm text-yellow-800 dark:text-yellow-200"
                data-testid="btn-hybridwalletconnect-submit"
              >
                You'll be disconnected from your current wallet (
                {truncateAddress(
                  phantomSession?.walletPublicKey ??
                    selectedWalletAccount?.address ??
                    ''
                )}
                ) when you connect to a new one.
              </p>
            </div>
          )}
          <div
            className="space-y-2 max-h-64 overflow-y-auto"
            data-testid="btn-hybridwalletconnect-submit"
          >
            {showPhantomMobile && (
              <PhantomSolanaMobileConnectButton
                onConnectStarted={() => setSelectedWalletAccount(undefined)}
              />
            )}
            {availableSolanaWallets.map((wallet) => (
              <WalletConnectButton
                key={wallet.name}
                wallet={wallet}
                onConnect={handleAccountsReceived}
              />
            ))}
          </div>
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label="Close"
              data-testid="btn-hybridwalletconnect-submit"
            >
              <Icons.Cross2Icon />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )

  // Toast notification component
  const ToastNotification = () => (
    <Toast.Provider swipeDirection="right">
      <Toast.Root
        className={`
          fixed bottom-4 right-4 z-50
          bg-white dark:bg-black rounded-none shadow-lg
          p-4 w-[350px] max-w-[95vw]
          data-[state=open]:animate-slideIn
          data-[state=closed]:animate-slideOut
          data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]
          data-[swipe=cancel]:translate-x-0
          data-[swipe=end]:animate-swipeOut
          border-l-4
          ${toastMessage.type === 'warning' ? 'border-yellow-500' : ''}
          ${toastMessage.type === 'success' ? 'border-green-500' : ''}
          ${toastMessage.type === 'info' ? 'border-blue-500' : ''}
        `}
        open={toastOpen}
        onOpenChange={setToastOpen}
        duration={toastMessage.type === 'success' ? 3000 : 5000}
        data-testid={`container-wallet-toast-${toastMessage.type}`}
      >
        <div
          className="flex items-start"
          data-testid="btn-hybridwalletconnect-submit"
        >
          {toastMessage.type === 'warning' && (
            <svg
              className="w-5 h-5 text-yellow-500 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              data-testid="btn-hybridwalletconnect-submit"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          )}
          <div>
            <Toast.Title
              className={`
                font-semibold text-base mb-1
                ${toastMessage.type === 'warning' ? 'text-yellow-800' : 'dark:text-gray-100 text-black'}
              `}
              data-testid="btn-hybridwalletconnect-submit"
            >
              {toastMessage.title}
            </Toast.Title>
            <Toast.Description
              className="text-gray-600 text-sm dark:text-gray-500"
              data-testid="btn-hybridwalletconnect-submit"
            >
              {toastMessage.description}
            </Toast.Description>
          </div>
        </div>
        <Toast.Close
          className="absolute top-2 right-2 p-1 rounded-full"
          data-testid="btn-hybridwalletconnect-submit"
        >
          <Icons.Cross2Icon />
        </Toast.Close>
      </Toast.Root>
      <Toast.Viewport className="fixed bottom-0 right-0 flex flex-col p-6 gap-2 w-[390px] max-w-[100vw] m-0 list-none z-50 outline-none" />
    </Toast.Provider>
  )

  // Function to disconnect current wallet
  const disconnectCurrentWallet = useCallback(async () => {
    if (phantomSession) {
      disconnectPhantom()
      showToast(
        'Wallet Disconnected',
        'Please select another wallet to connect',
        'info'
      )
      return
    }
    if (selectedWalletAccount && currentWallet) {
      try {
        // Get the disconnect function for this specific wallet
        const disconnectFn = walletDisconnectFns.get(currentWallet.name)
        if (disconnectFn) {
          // Use the disconnect function from the hook
          await disconnectFn()
          console.log('Wallet disconnected successfully')
        } else {
          console.warn(
            'No disconnect function found for wallet:',
            currentWallet.name
          )
        }
      } catch (error) {
        console.error('Error disconnecting wallet:', error)
      }

      // Clear the selected account from context (will also remove from localStorage)
      setSelectedWalletAccount(undefined)
      showToast(
        'Wallet Disconnected',
        'Please select another wallet to connect',
        'info'
      )
    }
  }, [
    selectedWalletAccount,
    currentWallet,
    disconnectPhantom,
    phantomSession,
    walletDisconnectFns,
    setSelectedWalletAccount,
    showToast,
  ])

  // Handle connect wallet button click (for disconnected state)
  const handleConnectWalletClick = useCallback(() => {
    setWalletDialogOpen(true)
  }, [])

  // EVM Transaction Handling
  const handleEVMTransaction = async (handler: () => Promise<void>) => {
    if (transactionState.status === 'completed') {
      showToast(
        'Transaction Already Complete',
        'This transaction has already been confirmed. Please do not send it again.',
        'warning'
      )
      return
    }
    showToast(
      'Transaction In Progress',
      'Please do not refresh the page or submit another transaction until this one completes.',
      'info'
    )
    try {
      await handler()
    } catch (error) {
      console.error(error)
      setToastOpen(false)
    }
  }

  // Effect to show toast on transaction completion
  useEffect(() => {
    if (transactionState.status === 'completed') {
      showToast(
        'Transaction Complete!',
        'Your transaction has been confirmed.',
        'success'
      )
    } else if (transactionState.status === 'failed') {
      showToast(
        'Transaction Failed',
        transactionState.error?.message ||
          'An error occurred during the transaction.',
        'warning'
      )
    }
  }, [transactionState, showToast])

  useEffect(() => {
    if (!phantomCallbackError) return
    setIsSolanaProcessing(false)
    showToast('Phantom Error', phantomCallbackError.message, 'warning')
    onError?.(phantomCallbackError)
  }, [onError, phantomCallbackError, showToast])
  if (!isSupported) {
    return (
      <div
        className="relative h-full"
        data-testid="btn-hybridwalletconnect-submit"
      >
        <Button
          disabled={true}
          className="inline-flex items-center justify-center py-6 px-4 w-full text-center bg-gray-200 dark:bg-gray-700 cursor-not-allowed"
          data-testid="btn-hybridwalletconnect-submit"
        >
          <span
            className="text-gray-500 dark:text-gray-400"
            data-testid="btn-hybridwalletconnect-submit"
          >
            Wallet payment unavailable
          </span>
        </Button>
        <ErrorMessage
          message={`${getNetworkName(network)} network is not supported`}
        />
        <ToastNotification />
      </div>
    )
  }

  // Determine whether to show Solana or EVM wallet UI
  if (isEVM) {
    return (
      <ConnectButton.Custom>
        {({ account, chain, openConnectModal, mounted }) => {
          if (!mounted) return null
          const ready = account && chain
          const connected = ready && !chain?.unsupported
          const wrongNetwork =
            connected && chainId !== getChainId(props.tokenSymbol)
          const isButtonDisabled =
            isPending ||
            isConfirming ||
            transactionState.status === 'mining' ||
            transactionState.status === 'completed'
          const handleClick = async () => {
            try {
              if (!connected) {
                openConnectModal()
              } else if (wrongNetwork) {
                try {
                  await switchChain({
                    chainId: getChainId(props.tokenSymbol),
                  })
                } catch (error) {
                  props.onError?.(error as Error)
                }
              } else {
                handleEVMTransaction(handleEvmTransfer)
              }
            } catch (error) {
              Sentry.captureException(error, {
                tags: {
                  action: 'wallet_button_click',
                  is_connected: connected ? 'true' : 'false',
                  wrong_network: wrongNetwork ? 'true' : 'false',
                  ready: ready ? 'true' : 'false',
                },
              })
              console.error('Button click error:', error)
              props.onError?.(error as Error)
            }
          }
          const getErrorMessage = () => {
            if (wrongNetwork)
              return `Please switch to ${getNetworkName(props.tokenSymbol.split('-')[1])}`
            if (transactionState.status === 'failed')
              return 'Transaction could not be processed at this time.  Please contact support at support@paytrie.com'
            return null
          }
          const buttonText =
            transactionState.status === 'completed'
              ? 'Transaction Complete!'
              : `Send ${props.amount} ${props.tokenSymbol}`
          const errorMsg = getErrorMessage()
          return (
            <div
              className="relative h-full"
              data-testid="btn-hybridwalletconnect-submit"
            >
              <div
                className="cursor-pointer"
                data-testid="btn-hybridwalletconnect-submit"
              >
                <button
                  onClick={handleClick}
                  disabled={isButtonDisabled}
                  className="w-full py-6 bg-black text-white dark:bg-white dark:text-black border-black text-center block"
                  style={{
                    height: 'auto',
                    fontFeatureSettings: '"ss01"',
                  }}
                  data-testid="btn-hybridwalletconnect-submit"
                >
                  <span data-testid="btn-hybridwalletconnect-submit">
                    {connected ? buttonText : t('wallet.payWithWallet')}
                  </span>
                </button>
                {getErrorMessage() && (
                  <ErrorMessage message={getErrorMessage()!} />
                )}
              </div>
              {errorMsg && <ErrorMessage message={errorMsg} />}
              <ToastNotification />
            </div>
          )
        }}
      </ConnectButton.Custom>
    )
  } else {
    const isCorrectPhantomWallet =
      phantomSession?.walletPublicKey === props.senderAddress
    const isCorrectStandardWallet =
      selectedWalletAccount?.address === props.senderAddress

    // Solana wallet UI
    return (
      <div
        className="relative h-full"
        data-testid="btn-hybridwalletconnect-submit"
      >
        {/* Conditional rendering of connected or disconnected state */}
        {isCorrectPhantomWallet ? (
          <ConnectedPhantomSolanaWalletButton
            isProcessing={isSolanaProcessing}
            props={props}
            setIsProcessing={setIsSolanaProcessing}
            showToast={showToast}
          />
        ) : isCorrectStandardWallet && selectedWalletAccount ? (
          <ConnectedSolanaWalletButton
            selectedAccount={selectedWalletAccount}
            props={props}
            showToast={showToast}
            setWalletDialogOpen={setWalletDialogOpen}
            disconnectCurrentWallet={disconnectCurrentWallet}
            isSolanaProcessing={isSolanaProcessing}
            setIsSolanaProcessing={setIsSolanaProcessing}
          />
        ) : (
          <>
            <button
              onClick={handleConnectWalletClick}
              className="inline-flex items-center h-full justify-center py-6 px-4 w-full text-center bg-black dark:bg-gray-200 text-white dark:text-black"
              data-testid="btn-hybridwalletconnect-submit"
            >
              <span data-testid="btn-hybridwalletconnect-submit">
                {t('wallet.payWithWallet')}
              </span>
            </button>
          </>
        )}

        <WalletSelectDialog />
        <ToastNotification />
      </div>
    )
  }
}
export default HybridWalletConnectButton

/*
IMPROVEMENT SUGGESTIONS:
1. Type safety issues - The ConnectButton.Custom callback has incorrect typings causing linter errors with chains/openChainModal
2. Separate EVM and Solana wallet logic into distinct components to reduce complexity
3. Implement proper retry/fallback mechanisms for failed wallet connections
4. Add comprehensive loading states with clear visual feedback during all wallet operations
5. Extract toast notifications into a separate context/provider to avoid duplication
6. Implement proper error boundaries for wallet connection failures
7. Add transaction simulation before sending to validate transactions will succeed
8. Consider implementing progressive enhancement for browsers without wallet support
9. Add more detailed logging for debugging wallet connection issues
10. Implement a more robust and consistent error handling strategy
11. Add proper focus management and keyboard navigation for better accessibility
12. The useDisconnect hook usage across wallets could be simplified with a unified approach
13. Abstract common wallet interaction patterns into custom hooks
14. Add unit tests for the wallet connection and transaction lifecycles
15. Implement proper transaction status monitoring with auto-polling
*/
